import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { dingDong } from "@/lib/notifySound";

const POLL_MS = 30000;

// Belletje in de topbar: rinkelt zodra er een nieuwe melding uit de controle-app komt.
export default function InspectionBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const seen = useRef(null);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/inspection/reports?handled=false");
        if (stop) return;
        setRows(data);
        const ids = new Set(data.map((r) => r.id));
        if (seen.current) {
          const fresh = data.filter((r) => !seen.current.has(r.id));
          if (fresh.length) {
            dingDong();
            toast.warning(`${t("inspection_new_report")}: ${fresh.map((r) => r.pitch_code).join(", ")}`,
              { duration: 8000, action: { label: t("open"), onClick: () => navigate("/admin/controle") } });
          }
        }
        seen.current = ids;
      } catch (e) { /* niet ingelogd of geen rechten */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stop = true; clearInterval(id); };
  }, [t, navigate]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  if (!rows.length) return null;

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((o) => !o)} data-testid="inspection-alert-bell" title={t("inspection_reports")}
        className="relative grid h-8 w-8 sm:h-9 sm:w-9 place-items-center rounded-full border border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/50 text-sky-700 dark:text-sky-300 transition-transform hover:scale-105 active:scale-95">
        <ShieldAlert className="h-4 w-4" />
        <span data-testid="inspection-alert-count"
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-600 text-white text-[10px] font-bold tabular-nums">
          {rows.length}
        </span>
      </button>

      {open && (
        <div data-testid="inspection-alert-panel"
          className="fixed left-3 right-3 top-[4.25rem] w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 max-h-96 overflow-y-auto cb-scroll rounded-2xl border border-border bg-card shadow-xl z-50 p-3 animate-in fade-in-0 zoom-in-95">
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300 mb-2">
            <ShieldAlert className="h-3.5 w-3.5" />{t("inspection_reports")}
          </p>
          <ul className="space-y-1.5">
            {rows.slice(0, 12).map((r) => (
              <li key={r.id} className="border-b border-border/60 pb-1.5 last:border-0 text-sm">
                <span className="font-semibold text-foreground">{r.pitch_code}</span>
                <span className="text-muted-foreground"> · {t(`insp_cat_${r.category}`)}</span>
                {r.note && <span className="block text-[11px] text-muted-foreground truncate">{r.note}</span>}
                <span className="block text-[10px] text-muted-foreground">{r.user_name} · {new Date(r.created_at).toLocaleTimeString()}</span>
              </li>
            ))}
          </ul>
          <button onClick={() => { setOpen(false); navigate("/admin/controle"); }} data-testid="inspection-alert-goto"
            className="mt-3 w-full rounded-full bg-primary text-primary-foreground text-xs font-semibold py-2 hover:opacity-90">
            {t("inspection_goto")}
          </button>
        </div>
      )}
    </div>
  );
}
